import { useEffect, useMemo, useRef, useState } from "react";
import { SearchSvg } from "../../svgFiles/SearchSvg";
import { CrossSvg } from "../../svgFiles/CrossSvg";
import { ChevronDownSvg } from "../../svgFiles/ChevronDownSvg";
import "../../features/admin/va-distributors/vaDistributors.css";

const HOSPITAL_SUFFIX_RE =
  /\b(va\s+medical\s+center|vamc|healthcare\s+system|health\s+care\s+system|medical\s+center|va\s+clinic|outpatient\s+clinic|clinic|campus|hospital|veterans?\s+affairs)\b/gi;

const STOPWORDS = new Set([
  "a",
  "an",
  "the",
  "of",
  "and",
  "at",
  "in",
  "for",
  "va",
  "us",
]);

function normalizeHospitalName(name) {
  return String(name || "")
    .trim()
    .toLowerCase()
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(HOSPITAL_SUFFIX_RE, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function significantTokens(normalized) {
  return String(normalized || "")
    .split(/\s+/)
    .filter((t) => t.length >= 2 && !STOPWORDS.has(t));
}

function scoreHospital(facilityNormalized, hospital) {
  if (!facilityNormalized || !hospital?.name) return 0;
  const hospitalKey = normalizeHospitalName(hospital.name);
  const withCity = normalizeHospitalName(
    [hospital.name, hospital.city].filter(Boolean).join(" ")
  );
  if (!hospitalKey) return 0;
  if (facilityNormalized === hospitalKey || facilityNormalized === withCity) {
    return 100;
  }

  let best = 0;
  for (const key of [hospitalKey, withCity].filter(Boolean)) {
    if (
      facilityNormalized.length >= 4 &&
      key.length >= 4 &&
      (facilityNormalized.includes(key) || key.includes(facilityNormalized))
    ) {
      const shorter = Math.min(facilityNormalized.length, key.length);
      const longer = Math.max(facilityNormalized.length, key.length);
      best = Math.max(best, Math.round(70 * (shorter / longer)));
    }
  }

  const facilityTokens = significantTokens(facilityNormalized);
  for (const key of [hospitalKey, withCity].filter(Boolean)) {
    const hospitalTokens = significantTokens(key);
    if (!facilityTokens.length || !hospitalTokens.length) continue;
    const set = new Set(hospitalTokens);
    const shared = facilityTokens.filter((t) => set.has(t));
    if (!shared.length) continue;
    const ratio =
      shared.length / Math.max(facilityTokens.length, hospitalTokens.length);
    const coverage =
      shared.length / Math.min(facilityTokens.length, hospitalTokens.length);
    const hasStrong = shared.some((t) => t.length >= 6);
    if (shared.length < 2 && !(hasStrong && coverage >= 0.75)) continue;
    if (ratio < 0.45 && coverage < 0.7) continue;
    best = Math.max(best, Math.round(35 + 34 * Math.max(ratio, coverage * 0.85)));
  }
  return best;
}

function buildLocalSuggestions(facilityName, stateGroups, limit = 5) {
  const normalized = normalizeHospitalName(facilityName);
  if (!normalized) return [];
  const scored = [];
  for (const group of stateGroups || []) {
    for (const hospital of group.hospitals || []) {
      const score = scoreHospital(normalized, hospital);
      if (score < 40) continue;
      scored.push({
        id: String(hospital.id),
        name: hospital.name || "",
        city: hospital.city || "",
        stateCode: group.stateCode || hospital.stateCode || "",
        stateName: group.stateName || hospital.stateName || "",
        matchScore: score,
        suggested: true,
      });
    }
  }
  return scored.sort((a, b) => b.matchScore - a.matchScore).slice(0, limit);
}

function formatHospitalLabel(hospital) {
  const name = hospital?.name || "Hospital";
  return hospital?.city ? `${name} (${hospital.city})` : name;
}

/**
 * Single-hospital picker for Create Order review.
 * State-wise hospital listing in one collapsible dropdown panel.
 */
export default function OrderHospitalSelect({
  value = "",
  onChange,
  stateGroups = [],
  isLoading = false,
  facilityName = "",
  suggestedHospitals = [],
  error = false,
  errorMessage = "",
  warningMessage = "",
  showWarning = false,
}) {
  const selectedId = String(value || "").trim();
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef(null);

  const hospitalLookup = useMemo(() => {
    const map = new Map();
    for (const group of stateGroups || []) {
      for (const hospital of group.hospitals || []) {
        map.set(String(hospital.id), {
          ...hospital,
          stateCode: group.stateCode,
          stateName: group.stateName,
        });
      }
    }
    return map;
  }, [stateGroups]);

  const suggestions = useMemo(() => {
    if (Array.isArray(suggestedHospitals) && suggestedHospitals.length) {
      return suggestedHospitals;
    }
    return buildLocalSuggestions(facilityName, stateGroups);
  }, [suggestedHospitals, facilityName, stateGroups]);

  const suggestionIds = useMemo(
    () => new Set(suggestions.map((s) => String(s.id))),
    [suggestions]
  );

  const filteredGroups = useMemo(() => {
    const term = query.trim().toLowerCase();

    return (stateGroups || [])
      .map((group) => {
        const hospitals = (group.hospitals || []).filter((hospital) => {
          if (!term) return true;
          const haystack = [
            hospital.name,
            hospital.city,
            group.stateName,
            group.stateCode,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return haystack.includes(term);
        });
        return { ...group, hospitals };
      })
      .filter((group) => (group.hospitals || []).length > 0);
  }, [stateGroups, query]);

  const selectedHospital = selectedId
    ? hospitalLookup.get(selectedId)
    : null;
  const selectedIsSuggested =
    Boolean(selectedId) && suggestionIds.has(selectedId);
  const appliedSuggestionRef = useRef(false);

  // Default the dropdown selection to the top suggested hospital once.
  useEffect(() => {
    if (appliedSuggestionRef.current) return;
    if (!suggestions.length) return;
    const id = String(suggestions[0]?.id || "").trim();
    if (!id || !hospitalLookup.has(id)) return;
    appliedSuggestionRef.current = true;
    if (id !== selectedId) onChange?.(id);
  }, [suggestions, hospitalLookup, selectedId, onChange]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen]);

  const selectHospital = (hospitalId) => {
    const id = String(hospitalId || "").trim();
    if (!id) return;
    appliedSuggestionRef.current = true;
    if (id === selectedId) {
      onChange?.("");
      return;
    }
    onChange?.(id);
    setIsOpen(false);
    setQuery("");
  };

  const triggerLabel = selectedHospital
    ? formatHospitalLabel(selectedHospital)
    : isLoading
      ? "Loading hospitals…"
      : "Select Hospitals";

  return (
    <div className="hospital-coverage-field order-hospital-select" ref={rootRef}>
      <label className="cmn_label va-review-bold-label d-block mb-2">
        States &amp; Hospitals <span className="text-danger">*</span>
      </label>
      {facilityName ? (
        <p className="hospital-coverage-field__hint mb-2">
          Facility name from invoice: <strong>{facilityName}</strong>
        </p>
      ) : (
        <p className="hospital-coverage-field__hint mb-2">
          No labeled FACILITY NAME on invoice — select hospital manually.
        </p>
      )}

      <button
        type="button"
        className={`order-hospital-select__trigger${
          error ? " order-hospital-select__trigger--error" : ""
        }${isOpen ? " is-open" : ""}`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        disabled={isLoading}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span
          className={`order-hospital-select__trigger-label${
            selectedHospital ? "" : " is-placeholder"
          }`}
        >
          {selectedIsSuggested ? (
            <span className="order-hospital-suggest__badge">Suggested</span>
          ) : null}
          <span>{triggerLabel}</span>
        </span>
        <span
          className={`order-hospital-select__chevron${isOpen ? " is-open" : ""}`}
          aria-hidden
        >
          <ChevronDownSvg />
        </span>
      </button>

      {isOpen ? (
        <div
          className={`order-hospital-select__panel${
            error ? " order-hospital-select__panel--error" : ""
          }`}
        >
          <div className="select-hospitals-modal__search">
            <SearchSvg />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Select Hospitals"
              aria-label="Search hospitals"
              autoFocus
            />
            {query ? (
              <button
                type="button"
                className="select-hospitals-modal__clear"
                onClick={() => setQuery("")}
                aria-label="Clear search"
              >
                <CrossSvg />
              </button>
            ) : null}
          </div>

          <div className="select-hospitals-list-head">
            <span>Hospital</span>
            <span>Status</span>
          </div>

          <div className="select-hospitals-list order-hospital-select__list">
            {isLoading ? (
              <p className="select-hospitals-list__empty">Loading hospitals…</p>
            ) : filteredGroups.length === 0 ? (
              <p className="select-hospitals-list__empty">No hospitals found.</p>
            ) : (
              filteredGroups.map((group) => (
                <div key={group.stateCode} className="select-hospitals-group">
                  <div className="select-hospitals-grid-row">
                    <div className="select-hospitals-row select-hospitals-row--state">
                      <span className="select-hospitals-row__name">
                        {group.stateName || group.stateCode}
                      </span>
                    </div>
                    <span />
                  </div>
                  {(group.hospitals || []).map((hospital) => {
                    const id = String(hospital.id);
                    const checked = id === selectedId;
                    const isSuggested = suggestionIds.has(id);
                    return (
                      <div key={id} className="select-hospitals-grid-row">
                        <label
                          className={`select-hospitals-row select-hospitals-row--hospital${
                            checked ? " is-selected" : ""
                          }`}
                        >
                          <input
                            type="radio"
                            className="select-hospitals-check"
                            name="order-hospital"
                            checked={checked}
                            onChange={() => selectHospital(id)}
                          />
                          <span className="select-hospitals-row__name">
                            {formatHospitalLabel(hospital)}
                            {isSuggested ? (
                              <span className="order-hospital-suggest__inline">
                                {" "}
                                · Suggested
                              </span>
                            ) : null}
                          </span>
                        </label>
                        <span className="select-hospitals-status">
                          {checked ? (
                            <span className="select-hospitals-status__badge">
                              Selected
                            </span>
                          ) : isSuggested ? (
                            <span className="order-hospital-suggest__badge">
                              Suggested
                            </span>
                          ) : null}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ))
            )}
          </div>

          <div className="order-hospital-select__footer">
            <span className="select-hospitals-modal__count">
              {selectedHospital
                ? formatHospitalLabel(selectedHospital)
                : "No hospital selected"}
            </span>
            {selectedId ? (
              <button
                type="button"
                className="hospital-coverage-field__link-btn"
                onClick={() => onChange?.("")}
              >
                Clear
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {showWarning && warningMessage ? (
        <p className="va-field-hint va-field-hint--warning small mb-0 mt-2">
          {warningMessage}
        </p>
      ) : null}
      {error && errorMessage ? (
        <p className="text-danger small mb-0 mt-2">{errorMessage}</p>
      ) : null}
    </div>
  );
}
